import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
  HTTP_INTERCEPTORS
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { APP_ROUTES } from '@app/app.routes.config';
import { AuthService } from '@services/auth.service';
import { LocalStorageService } from '@services/local-storage.service';
import { SnackBarService } from '@services/snack-bar.service';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private tokenService = inject(LocalStorageService);
  private authService = inject(AuthService);
  private snackBarService = inject(SnackBarService);

  private isRefreshing = false;

  private refreshTokenSubject: BehaviorSubject<{ accessToken: string; refreshToken: string } | null> =
    new BehaviorSubject<{ accessToken: string; refreshToken: string } | null>(null);

  intercept(req: HttpRequest<string | object>, next: HttpHandler): Observable<HttpEvent<object>> {
    return next.handle(req).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401 && !req.url.endsWith('logout')) {
          if (!req.url.endsWith('refresh')) {
            return this.handle401Error(req, next);
          } else {
            this.authService.logOut();
            this.snackBarService.showSnackBar('Your session is expired');
            return throwError(() => error);
          }
        } else {
          return throwError(() => error);
        }
      })
    );
  }

  private handle401Error(request: HttpRequest<string | object>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const token = this.tokenService.getRefreshToken();

      const authReq = request.clone({
        setHeaders: {
          Authorization: 'Bearer ' + token
        }
      });

      return this.authService.refreshToken({ headers: authReq.headers }).pipe(
        switchMap((token: { accessToken: string; refreshToken: string }) => {
          this.isRefreshing = false;

          this.tokenService.setToken(token.accessToken);
          this.tokenService.setRefreshToken(token.refreshToken);
          this.refreshTokenSubject.next(token);

          const authReq = request.clone({
            headers: request.headers.set('Authorization', 'Bearer ' + token.accessToken)
          });

          return next.handle(authReq);
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.authService.broadcastLogout();
          this.tokenService.clear();
          this.redirectToLogin();
          return throwError(() => err);
        })
      );
    }

    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const authReq = request.clone({
          headers: request.headers.set('Authorization', 'Bearer ' + token.accessToken)
        });
        return next.handle(authReq);
      })
    );
  }

  private redirectToLogin(): void {
    const adminRoute = this.authService.isAdmin() ? '/' + APP_ROUTES.ADMIN : '';
    window.location.href = '/' + APP_ROUTES.AUTH + '/login' + adminRoute;
  }
}

export const authInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }];
